import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, NavigationStart, Router, RouterEvent, Event } from "@angular/router";

import { DeviceDetectorService } from "ngx-device-detector";
import { BehaviorSubject, filter, Subscription } from "rxjs";
import { MenuItem } from "primeng/api";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import * as moment from "moment";
import CryptoES from "crypto-es";

import {
    ProjectSettings,
    PwaSettings,
    SettingsSection,
    SettingsSectionsName,
    SystemConfigurationService,
    Theme,
    ThemeTemplate
} from "@main/system-configuration";
import { LoadingTime, LoadingTimeService, LoadingTimeSettings } from "@main/loading-time";
import { AccountService } from "@account/services";
import { CurrentUserChangedData, LinkUserService, PictureMode, UserService } from "@main/users";
import { AppStorage } from "@bbwt/utils/app-storage";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { isLocalhost } from "@bbwt/utils";
import { RuntimeEditorService, RuntimeEditorUi } from "@main/runtime-editor";
import { FeedbackService } from "@main/feedback";
import { IRealUser } from "@bbwt/interfaces";
import { SystemData, SystemDataService, Version } from "@bbwt/modules/system-data";
import { FooterMenuService, IMainMenuItem, MainMenuService } from "@main/menu-designer";
import { StaticPageService } from "@main/static-pages";
import { UserHelperService } from "@main/users/user-helper.service";
import { RoutesService } from "@main/routes";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { PatientService } from "./patient-switch/patient.service";
import { CoreRole } from "@main/roles/core-role";
import { FormIOModuleService } from "@main/formio/services/formio-module.service";
import { FormIODefinition } from "@features/bb-formio";
import { IEpisode } from "@project/models/episodes/episode";
import { FormModuleDTO } from "@main/formio/dto/formModuleDTO";
import { EpisodeSelectMode } from "@project/episode/episode-select/episode-select-mode";
import { OrganizationService } from "@main/companies";
import { MultiUserFormDef } from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";
import { EpisodeService } from "@project/episode/episode.service";
import { MultiUserFormService } from "@main/formio/multi-user-form/services/multi-user-form.service";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { MultiUserFormAssociation, NewMultiUserFormAssociation } from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { FormioViewerService } from "@main/formio/services/formioViewer.service";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import { MultiUserFormAssociationsService } from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import { CustomComponentTypes } from "@main/formio/components/data-insertion-dialog/customComponentTypes";
import { ModuleReportsService } from "@project/module-reports/module-reports.service";
import { EnhancedRouterService } from "@mipro/services/enhanced-router.service";
import { MiProModules } from "@mipro/classes/mipro-modules";

@Component({
    selector: "app",
    templateUrl: "./app.component.html"
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    currentTheme: Theme;
    projectName: string;
    avatarUrl: string;
    currentUserName: string;
    currentUserInitials: string;
    isUserImpersonate = false;
    isPatient: boolean;
    menuModel: IMainMenuItem[];
    formIoMenuModel: IMainMenuItem[] = [];
    footerMenuModel: MenuItem[];
    recordLoadingTime: boolean;
    loadTime: number;
    loadingTimeStart: Date;
    loadingTime: LoadingTime;
    feedbackUserEmail: string;
    feedbackUserFirstName: string;
    feedbackUserLastName: string;
    devToolsDialogVisible = false;
    simulatedCodeOptions = [
        { label: "502", value: 502 },
        { label: "503", value: 503 },
        { label: "504", value: 504 }
    ];
    simulatedHttpStatusCode: number;
    private formIoLoadingSubject = new BehaviorSubject<boolean>(true);
    formIoLoading = this.formIoLoadingSubject.asObservable();
    httpStatusCodeSimulationEnabled: boolean;
    offlineIndicatorVisible: boolean;
    showSystemDataDialog: boolean;
    systemData: SystemData;
    pageLoadTime: string;
    versionInfo: Version;

    proceed: boolean = false;

    private readonly versionDataKey = "version-data";
    private subscriptions: Subscription[] = [];

    currentFormModule: FormModuleDTO;
    currentFormDef: FormIODefinition;
    currentMUFDef: MultiUserFormDef = null;

    episodeSelectionModes: EpisodeSelectMode[] = [EpisodeSelectMode.NewEpisode, EpisodeSelectMode.ExistingEpisode];

    displaySelectFormDialog: boolean = false;
    displaySelectEpisodeDialog: boolean = false;
    displaySelectPatientDialog: boolean = false;
    displayMufCreateInstanceDialog: boolean = false;
    mufAssoc: MultiUserFormAssociation = null;
    newMufAssocData: NewMultiUserFormAssociation = null;

    episodeRequired: boolean = false;
    restoreSelectControlState = false;
    restoreDuplicateCheakState = false;
    currentEpisode: IEpisode = null;

    displayNewFormRequestDialog = false;
    currentUrl: string = null;

    constructor(
        public runtimeEditorUi: RuntimeEditorUi,
        public runtimeEditorService: RuntimeEditorService,
        public feedbackService: FeedbackService,
        private router: Router,
        private deviceDetectorService: DeviceDetectorService,
        private broadcastService: BroadcastService,
        private systemConfigurationService: SystemConfigurationService,
        private systemService: SystemDataService,
        private menuService: MainMenuService,
        private footerMenuService: FooterMenuService,
        private accountService: AccountService,
        private userService: UserService,
        private patientService: PatientService,
        private userHelperService: UserHelperService,
        private messageService: MessageService,
        private loadingTimeService: LoadingTimeService,
        private routesService: RoutesService,
        private formIoModuleService: FormIOModuleService,
        private appOnlineStateService: AppOnlineStateService,
        private formIODefinitionService: FormIODefinitionService,
        private episodeService: EpisodeService,
        private multiUserFormService: MultiUserFormService,
        private formioViewerService: FormioViewerService,
        private formIODataService: FormIODataService,
        private multiUserFormAssociationService: MultiUserFormAssociationsService,
        private moduleReportsService: ModuleReportsService,
        private enhancedRouterService: EnhancedRouterService
    ) {
        this.subscriptions.push(
            broadcastService.on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName).subscribe(data => {
                if (data.isLogout) {
                    const lastLoggedUserName = AppStorage.getItem(AppStorage.LastLoggedUserName);
                    this.currentUserName = lastLoggedUserName || "";
                } else {
                    this.refreshCurrentUser();
                    this.refreshImpersonationState();
                }
            })
        );

        this.subscriptions.push(
            broadcastService.on(UserService.CurrentUserEmailChangedEventName).subscribe(() => {
                if (!AppStorage.getItem<boolean>(AppStorage.NhsLoginKey)) {
                    AppStorage.setItemForSession(
                        AppStorage.LogoutReasonMessageKey,
                        "New verification is required after the email address change."
                    );
                    AppStorage.setItemForSession(AppStorage.LastVisitedPageUrlKey, location.pathname);
                    accountService.logout();
                }
            })
        );

        this.subscriptions.push(
            broadcastService
                .on(SystemConfigurationService.SettingsSectionChangedEventName)
                .subscribe((settingsSection: SettingsSection) => {
                    if (settingsSection.sectionName == SettingsSectionsName.ProjectSettings) {
                        this.setProjectSettings(settingsSection.value as ProjectSettings);
                    }
                    if (settingsSection.sectionName == SettingsSectionsName.ProjectSettings) {
                        this.setProjectSettings(settingsSection.value as ProjectSettings);
                    }
                })
        );

        this.subscriptions.push(
            broadcastService.on<Theme>(SystemConfigurationService.ThemeChangedEventName).subscribe(theme => {
                if (this.currentTheme.template == theme.template) {
                    this.currentTheme = theme;
                } else {
                    location.reload();
                }
            })
        );

        this.subscriptions.push(
            broadcastService.on(LinkUserService.UserSelectedOrganizationChangedEventName).subscribe(() => {
                if (this.router.url.includes("app/formio")) {
                    this.router.navigateByUrl("/app").then(_ => window.location.reload());
                }
            })
        );

        this.currentTheme = systemConfigurationService.theme;
        this.versionInfo = AppStorage.getItem<Version>(this.versionDataKey);
    }

    get _themeTemplateEnum(): any {
        return ThemeTemplate;
    }

    get logoImageUrl(): string {
        return this.systemConfigurationService.logoImageUrl;
    }

    get runtimeEditorAvailable(): boolean {
        return this.userHelperService.isSuperAdmin;
    }

    get patientSwitchAvailable(): boolean {
        return !this.userHelperService.isPatient && !this.userHelperService.isGenericPatient;
    }

    get isClinicalSafetyOfficer(): boolean {
        return this.userHelperService.hasRole(CoreRole.ClinicalSafetyOfficer);
    }

    get isGenericPatient() {
        return this.userHelperService.hasRole(CoreRole.GenericPatientLogin);
    }

    get isGenericLogin() {
        return this.userHelperService.hasRole(CoreRole.GenericLogin);
    }

    get feedbackAvailable(): boolean {
        return this.feedbackService.enabled;
    }

    get devToolsAvailable(): boolean {
        return isLocalhost();
    }

    get loadingTimeAvailable(): boolean {
        return this.recordLoadingTime;
    }

    get systemDataInfoAvailable(): boolean {
        return this.userHelperService.isSuperAdmin;
    }

    ngOnInit(): void {
        const loggedOffline = AppStorage.getItem(AppStorage.LoggedOfflineKey);
        if (!this.appOnlineStateService.isAppOnline && loggedOffline != this.userService.currentUser.offlinePin) {
            // Offline support
            this.router.navigateByUrl("/account/login");
        } else {
            this.proceed = true;
            this.initSystemConfigurationSettings();
            this.initMenu();
            this.initFeedbackPersonalData();
            this.initVersionInfo();
            this.initLoadingTime();
            this.refreshCurrentUser();
            this.refreshImpersonationState();
            this.initAttemptedUrl();
        }
        this.currentUrl = this.router.url;
    }

    ngAfterViewInit(): void {
        this.pageLoadTime = moment().toLocaleString();
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(x => x?.unsubscribe());
    }

    logout(): void {
        this.accountService.logout().then(() => this.router.navigateByUrl("/account/login"));
    }

    stopImpersonation(): void {
        this.userService.stopImpersonationForCurrentUser().then(() => {
            this.router.navigate(["/"]);
        });
    }

    runtimeEditorToggle() {
        if (this.runtimeEditorUi.isEditorOn) {
            if (this.runtimeEditorUi.hasNewChanges) {
                return true;
            } else {
                this.runtimeEditorUi.editorOff();
            }
        } else {
            this.runtimeEditorUi.editorOn();
        }
        return false;
    }

    runtimeEditorClose() {
        this.runtimeEditorUi.editorOff();
    }

    runtimeEditorCancel() {
        this.runtimeEditorUi.cancelNewChanges();
    }

    runtimeEditorOn() {
        this.runtimeEditorUi.editorOn();
    }

    runtimeEditorSave() {
        this.runtimeEditorUi.sendToGit(false);
    }

    showFeedback(): void {
        window.dispatchEvent(new Event("openFeedbackIframe"));
    }

    showDevToolsDialog(): void {
        this.devToolsDialogVisible = true;
    }

    toggleResponseStatusCodeSimulation($event): void {
        if ($event) {
            AppStorage.setItem(AppStorage.SimulatedHttpStatusCodeKey, this.simulatedHttpStatusCode);
            this.httpStatusCodeSimulationEnabled = true;
        } else {
            AppStorage.setItem(AppStorage.SimulatedHttpStatusCodeKey, null);
            this.httpStatusCodeSimulationEnabled = false;
        }
    }

    showSystemDataInfoDialog(): void {
        this.showSystemDataDialog = true;
        this.systemService.getData().then(systemData => {
            this.systemData = systemData;
            this.systemData.currentUrl = this.router.url;
            this.systemData.pageLoadTime = this.pageLoadTime;
        });
    }

    sendDebugRequest(): void {
        this.systemService.getDebugData().then(() => {
            this.messageService.add(Message.Success("Request sent. See response in Network tab of browser's debugger.", "Debug Request"));
        });
    }

    sendApiExceptionsRequest(): void {
        this.systemService.getApiExceptionsData().then(() => {
            this.messageService.add(
                Message.Success("Request sent. See response in Network tab of browser's debugger.", "API Exceptions Request")
            );
        });
    }

    private initSystemConfigurationSettings(): void {
        this.setLoadingTimeSettings(
            this.systemConfigurationService.getSettingsSection<LoadingTimeSettings>(SettingsSectionsName.LoadingTimeSettings)
        );
        this.setPwaSettings(this.systemConfigurationService.getSettingsSection<PwaSettings>(SettingsSectionsName.PwaSettings));
        this.setProjectSettings(this.systemConfigurationService.getSettingsSection<ProjectSettings>(SettingsSectionsName.ProjectSettings));

        this.broadcastService.on<SettingsSection>(SystemConfigurationService.SettingsSectionChangedEventName).subscribe(settingsSection => {
            if (settingsSection.sectionName == SettingsSectionsName.LoadingTimeSettings) {
                this.setLoadingTimeSettings(<LoadingTimeSettings>settingsSection.value);
            }
            if (settingsSection.sectionName == SettingsSectionsName.PwaSettings) {
                this.setPwaSettings(<PwaSettings>settingsSection.value);
            }
            if (settingsSection.sectionName == SettingsSectionsName.ProjectSettings) {
                this.setProjectSettings(<ProjectSettings>settingsSection.value);
            }
        });
    }

    private setLoadingTimeSettings(loadingTimeSettings: LoadingTimeSettings): void {
        if (!loadingTimeSettings) return;

        this.recordLoadingTime = loadingTimeSettings.recordLoadingTime;
    }

    private setPwaSettings(pwaSettings: PwaSettings): void {
        if (!pwaSettings) return;

        this.offlineIndicatorVisible =
            !pwaSettings ||
            (this.deviceDetectorService.isDesktop() && pwaSettings.desktopShowIndicator) ||
            (!this.deviceDetectorService.isDesktop() && pwaSettings.mobileShowIndicator);
    }

    private setProjectSettings(projectSettings: ProjectSettings): void {
        if (!projectSettings) return;

        this.projectName = projectSettings.name;
    }

    private initMenu(): void {
        this.refreshMenu(false);

        this.subscriptions.push(this.broadcastService.on(MainMenuService.MainMenuChangedEventName).subscribe(() => this.refreshMainMenu()));
        this.subscriptions.push(
            this.broadcastService.on(FooterMenuService.FooterMenuChangedEventName).subscribe(() => this.refreshFooterMenu())
        );
        this.subscriptions.push(
            this.broadcastService.on(StaticPageService.StaticPagesChangedEventName).subscribe(() => this.refreshMenu(false))
        );
        this.subscriptions.push(
            this.broadcastService
                .on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName)
                .subscribe(data => this.refreshMenu(true, data))
        );
        this.subscriptions.push(
            this.broadcastService
                .on(OrganizationService.OrganizationChangedEventName)
                .subscribe(_ => this.refreshMenu(false))
        );
        this.subscriptions.push(
            this.broadcastService
                .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
                .subscribe(_ => this.refreshMenu(false))
        );
        this.subscriptions.push(
            this.broadcastService.on<any>(OrganizationService.ModulesChangedEventName).subscribe(() => this.loadFormIoMenuItems())
        );
    }

    private refreshMenu(checkIfUserChanged = true, currentUserChangedData?: CurrentUserChangedData): void {
        if (
            !checkIfUserChanged ||
            !currentUserChangedData ||
            currentUserChangedData.identityChanged ||
            currentUserChangedData.accessRightsChanged
        ) {
            this.refreshMainMenu();
            this.refreshFooterMenu();
        }
    }

    private async refreshMainMenu() {
        if (this.userService.isLogged) {
            if (!this.menuModel || this.appOnlineStateService.isAppOnline) {
                this.menuModel = await this.menuService.getForCurrentUser();
                this.correctReportMenus();
                this.correctSelfClickBehavior();
            }
            if (!this.appOnlineStateService.isAppOnline) {
                this.menuModel = this.menuModel.map(m => this.getOfflineMenu(m));
            }

            if (this.appOnlineStateService.isAppOnline) {
                await this.loadFormIoMenuItems();
            }
        } else {
            this.menuModel = null;
        }
    }

    private correctReportMenus() {
        this.menuModel.forEach(menuItem => {
            const index = menuItem.children?.findIndex(c => c.routerLink?.startsWith("/app/module-reports/")) ?? -1;
            if (index != -1) {
                const child = menuItem.children[index];
                menuItem.children[index] = {
                    ...child,
                    customHandler: "click",
                    onClick: async () => {
                        this.enhancedRouterService.forceNavigate([child.routerLink]);
                    },
                    referenceLink: child.routerLink,
                    routerLink: null
                };
            }
        });
    }

    // Allows reloading the current route when clicking its menu item
    private correctSelfClickBehavior() {
        const linksToCorrect = [
            "/app/formio/builder"
        ];

        this.menuModel.forEach(menuItem => {
            if (!menuItem.children?.length) return;

            const childToCorrectIndex = menuItem.children.findIndex(child =>
                linksToCorrect.some(link => child.routerLink?.startsWith(link))
            );

            if (childToCorrectIndex === -1) return;

            const originalChild = menuItem.children[childToCorrectIndex];
            const originalRouterLink = originalChild.routerLink;

            menuItem.children[childToCorrectIndex] = {
                ...originalChild,
                customHandler: "click",
                onClick: async () => {
                    this.enhancedRouterService.forceNavigate([originalRouterLink]);
                },
                routerLink: null
            };
        });
    }

    setFormIoLoading(value: boolean): void {
        this.formIoLoadingSubject.next(value);
    }

    private async loadFormIoMenuItems() {
        this.setFormIoLoading(true);
        this.formIoMenuModel = [];

        const modules = await this.formIoModuleService.getAll();
        const showReports = this.userHelperService.hasModuleEnabled(MiProModules.Reporting)

        const publishedStatuses = await this.moduleReportsService.modulesHasDashboardPublished(modules.map(m => m.name));
        const moduleHasPublishedMap = new Map<string, boolean>();
        publishedStatuses.forEach(item => {
            moduleHasPublishedMap.set(item.moduleName, item.hasDashboardPublished);
        });

        modules.forEach(async formioModuleDto => {
            let queryParams = {
                userId: this.userHelperService.userId,
                category: formioModuleDto?.name
            };
            const url = this.router.serializeUrl(this.router.createUrlTree(["app/formio/form-instances-all"], { queryParams }));

            const hasModule = this.userHelperService.hasModuleEnabled(formioModuleDto.name);
            const hasModuleReadPermission = this.userHelperService.hasPermission(`${formioModuleDto.name} - Read`);
            const hasModuleWritePermission = this.userHelperService.hasPermission(`${formioModuleDto.name} - Write`);
            const hasFormDefinitions = !!formioModuleDto?.hasFormDefinitions;
            const hasDashboardPublished = moduleHasPublishedMap.get(formioModuleDto.name) || false;

            this.formIoMenuModel.push({
                label: formioModuleDto.name,
                hidden: (!this.userHelperService.isPatient && !hasModuleReadPermission) || !hasModule,
                miproIcon: "mi-clinical-notes",
                children: [
                    {
                        label: "New",
                        hidden: !hasModuleWritePermission,
                        disabled: !hasFormDefinitions,
                        icon: "add",
                        customHandler: "click",
                        onClick: () => this.onNewFormClicked(formioModuleDto)
                    },
                    {
                        label: "Review",
                        customHandler: "click",
                        onClick: async () => {
                            this.enhancedRouterService.forceNavigate(["/app/formio/form-instances-all"], { queryParams });
                        },
                        referenceLink: url,
                        icon: "check",
                        hidden: !hasModuleReadPermission && !this.userHelperService.isPatient
                    },
                    {
                        label: "Reports",
                        customHandler: "click",
                        onClick: async () => {
                            this.enhancedRouterService.forceNavigate(["/app/module-reports/" + formioModuleDto.name]);
                        },
                        referenceLink: "/app/module-reports/" + formioModuleDto.name,
                        hidden: !showReports || !hasDashboardPublished || !hasModuleReadPermission || !hasModuleWritePermission
                    }
                ]
            } as IMainMenuItem);
        });

        this.setFormIoLoading(false);
    }

    getOfflineMenu(menu: any): any {
        menu.children = menu.children.map(m => this.getOfflineMenu(m));
        menu.hidden =
            (menu.routerLink != null && !this.appOnlineStateService.offlineAllowedRoutes.includes(menu.routerLink)) ||
            (menu.children.length > 0 && menu.children.every(m => m.hidden));
        return menu;
    }

    private refreshFooterMenu(): void {
        if (this.userService.isLogged) {
            this.footerMenuService
                .getAll()
                .then(items => (this.footerMenuModel = items.map(x => <MenuItem>{ label: x.name, routerLink: x.routerLink })));
        } else {
            this.footerMenuModel = null;
        }
    }

    private initFeedbackPersonalData(): void {
        const realUser = AppStorage.getItem<IRealUser>(AppStorage.RealUserKey);
        if (realUser && (realUser.email || realUser.firstName || realUser.lastName)) {
            this.feedbackUserEmail = realUser.email;
            this.feedbackUserFirstName = realUser.firstName;
            this.feedbackUserLastName = realUser.lastName;
        } else {
            this.feedbackUserEmail = this.userService.currentUser.email;
            this.feedbackUserFirstName = this.userService.currentUser.firstName;
            this.feedbackUserLastName = this.userService.currentUser.lastName;
        }
    }

    private initVersionInfo(): void {
        this.systemService.getVersionInfo().then(version => {
            this.versionInfo = version;
            AppStorage.setItem(this.versionDataKey, version);
        });
    }

    private saveLoadingTime() {
        if (!this.userService.isLogged) return;

        if (this.recordLoadingTime) {
            this.loadTime = (new Date().getTime() - this.loadingTimeStart.getTime()) / 1000;
            this.loadingTime = new LoadingTime();
            this.loadingTime.time = this.loadTime * 1000;
            this.loadingTime.route = this.router.url;
            this.loadingTime.dateTime = new Date();
            this.loadingTime.account = this.userService.currentUser.email;
            this.loadingTime.userAgent = window.navigator.userAgent;
            this.loadingTimeService.create(this.loadingTime);
        }
    }

    private initLoadingTime() {
        this.subscriptions.push(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.loadingTimeStart = new Date();
                }

                if (event instanceof NavigationEnd) {
                    this.saveLoadingTime();
                }
            })
        );

        this.subscriptions.push(
            this.loadingTimeService.change.subscribe(save => {
                if (save) {
                    this.saveLoadingTime();
                } else {
                    this.loadingTimeStart = new Date();
                }
            })
        );
    }

    private initAttemptedUrl() {
        this.router.events
            .pipe(filter((e: Event | RouterEvent): e is RouterEvent => e instanceof RouterEvent))
            .subscribe((e: RouterEvent) => {
                this.routesService.attemptedUrl = e.url;
            });
    }

    private refreshCurrentUser(): void {
        if (!this.userService.isLogged) return;

        switch (this.userService.currentUser.pictureMode) {
            case PictureMode.Empty:
                this.avatarUrl = "assets/images/noavatar.png";
                break;
            case PictureMode.Gravatar:
                this.avatarUrl = "https://www.gravatar.com/avatar/" + CryptoES.MD5(this.userService.currentUser.gravatarEmail).toString();
                break;
            case PictureMode.Upload:
                this.avatarUrl = this.userService.currentUser.avatarImage?.url;
                break;
        }

        this.userHelperService.refresh();
    }

    private refreshImpersonationState(): void {
        const impersonationData = this.userService.impersonationData;
        this.isUserImpersonate = impersonationData?.isImpersonating;
        if (this.isUserImpersonate) {
            this.currentUserName = `${impersonationData.impersonatedUserName} via ${impersonationData.originalUserName}`;
        } else {
            this.currentUserName = this.userService.currentUser?.fullName;
            this.currentUserInitials =
                this.userService.currentUser?.firstName[0].toUpperCase() + this.userService.currentUser?.lastName[0].toUpperCase();
        }
    }

    private get gitProjectUrlBase(): string {
        return "https://gitlab.bbconsult.co.uk/blueberry/" + this.versionInfo?.projectName;
    }

    private get showVersionInfo(): boolean {
        return !!this.versionInfo?.productVersion;
    }

    public isAppOnline() {
        return this.appOnlineStateService.isAppOnline;
    }

    redirectToFormioForm(form: FormIODefinition, episode: IEpisode) {
        let queryParams = {
            formId: form.id,
            revisionId: form.activeRevisionId,
            patientId: this.patientService.currentPatientId,
            episodeId: episode.id
        };

        const url = this.router.serializeUrl(this.router.createUrlTree(["app/formio/display"], { queryParams }));

        this.router.navigateByUrl(url);
    }

    onNewFormClicked(formModule: FormModuleDTO) {
        this.currentFormModule = formModule;

        this.displaySelectFormDialog = true;
    }

    showSelectEpisodeDialog() {
        this.displaySelectEpisodeDialog = true;
    }

    onFormIoEpisodeSelected(episode: IEpisode) {
        this.currentEpisode = episode;
        this.displaySelectEpisodeDialog = false;
        this.displaySelectFormDialog = true;
    }

    async formSelected(selectedDefinition: FormIODefinition) {
        this.currentFormDef = selectedDefinition;
        this.displaySelectFormDialog = false;

        if (!this.patientService.currentPatientId) {
            this.displaySelectPatientDialog = true;
            return;
        }
        await this.prepareEpisode();
    }

    async prepareEpisode() {
        const formObject = await this.formIODefinitionService.get(this.currentFormDef.id);
        const episodeForm = formObject.episode;
        this.episodeRequired = formObject.isEpisodeLocked;

        if (!this.restoreSelectControlState && !this.restoreDuplicateCheakState) {
            this.savingDataLocally();
        }

        if (episodeForm) {
            this.currentEpisode = await this.formIODefinitionService.ensureInstanceEpisodeExistsForPatient(
                episodeForm,
                this.patientService.currentPatientId
            );
        }
        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;

        if (this.currentFormDef.byRequestOnly) {
            this.displayNewFormRequestDialog = true;
            return;
        }

        if (this.currentFormDef.activeRevision?.mufCapable) {
            this.currentMUFDef = await this.multiUserFormService.getByFormRevisionId(this.currentFormDef.activeRevisionId);
            this.displayMufCreateInstanceDialog = true;
            return;
        }
        this.navigateToFormDisplay();
    }

    async navigateToFormDisplay() {
        let queryParams = {};

        if (this.mufAssoc) {
            this.redirectToMUFInstance();
            return;
        }

        const latestRevisionForCurrentForm = await this.formioViewerService.get(this.currentFormDef.id);

        if (this.currentEpisode) {
            queryParams["episodeId"] = this.currentEpisode.id;
        }
        queryParams["formId"] = this.currentFormDef.id;
        queryParams["revisionId"] = latestRevisionForCurrentForm.activeRevision.id;
        queryParams["patientId"] = this.patientService.currentPatientId;
        queryParams["returnUrl"] = this.currentUrl;

        const componentTypes: CustomComponentTypes = await this.formIODefinitionService.getCustomComponentTypes(this.currentFormDef.id);
        if (
            componentTypes &
            (CustomComponentTypes.ConsultationHistoryV2 |
                CustomComponentTypes.ConsultationHistory |
                CustomComponentTypes.EpisodeNotes |
                CustomComponentTypes.MedicationHistory)
        ) {
            await this.redirectToDataInsertion(queryParams);
            return;
        }

        this.router.navigate(["app/formio/display"], { queryParams });
    }

    async onSkipOkEpisodeSelectDialog(episode: IEpisode = null) {
        this.currentEpisode = episode;
        this.displaySelectEpisodeDialog = false;

        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;

        if (this.newMufAssocData) {
            this.newMufAssocData.episodeId = episode.id;
            this.newMufAssocData.isReadOnlyEpisode = true;
            this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        }
        this.navigateToFormDisplay();
    }
    onCancelEpisodeSelectDialog() {
        this.newMufAssocData = this.mufAssoc = null;
        this.deleteDtaftEpisode();
        this.currentEpisode = null;
        this.displaySelectEpisodeDialog = false;
        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;
    }

    async deleteDtaftEpisode() {
        if (!this.currentEpisode || this.currentEpisode?.state != EpisodeStatus.FormDraft) {
            return;
        }
        await this.episodeService.deleteEpisode(this.currentEpisode.id, <IHttpResponseHandlerSettings>{
            showSuccessMessage: false
        });
        this.displaySelectEpisodeDialog = false;
    }

    onCancelCreateInstanceDialog() {
        this.deleteDtaftEpisode();
        this.currentMUFDef = null;
        this.displayMufCreateInstanceDialog = false;
    }

    onOkSelectPatientDialog(patientId: string) {
        this.patientService.currentPatientId = patientId;
        this.displaySelectPatientDialog = false;
        this.formSelected(this.currentFormDef);
    }

    async onCreateInstanceDialog(mufAssocData: NewMultiUserFormAssociation) {
        this.newMufAssocData = mufAssocData;
        if (!this.restoreSelectControlState && !this.restoreDuplicateCheakState) {
            this.savingDataLocally();
        }

        var selfTargetStage = this.newMufAssocData.multiUserFormAssociationLinks.some(x => x.userId == this.userService.currentUser.id);
        if (!this.currentEpisode && this.episodeRequired && !selfTargetStage) {
            this.displaySelectEpisodeDialog = true;
            return;
        }

        this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        this.navigateToFormDisplay();
    }

    redirectToMUFInstance() {
        let startMufSelf =
            this.mufAssoc.multiUserFormAssociationLinks.find(x => x.multiUserFormStage.sequenceStepIndex == 1)?.userId ==
            this.userService.currentUser.id;
        this.currentMUFDef = null;
        this.currentEpisode = null;
        if (startMufSelf) {
            let queryParams = {};
            queryParams["mufId"] = this.mufAssoc.id;
            queryParams["target"] = this.userService.currentUser.id;
            queryParams["formDataId"] = this.mufAssoc.formDataId;
            queryParams["returnUrl"] = this.currentUrl;
            this.router.navigate(["app/formio/multiuser/display"], { queryParams });
        }
        this.mufAssoc = null;
    }

    onCancelFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
    }

    onCreateFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
        this.displaySelectEpisodeDialog = false;
        this.displaySelectFormDialog = false;
    }

    savingDataLocally() {
        AppStorage.setItemForSession("newMufAssocData", this.newMufAssocData);
        AppStorage.setItemForSession("currentFormModule", this.currentFormModule);
        AppStorage.setItemForSession("formDataId", this.currentFormDef.id);
        AppStorage.setItemForSession("episode", this.currentEpisode);
        AppStorage.setItemForSession("episodeRequired", this.episodeRequired);
    }

    viewEpisode() {
        this.displaySelectFormDialog = false;
        this.displaySelectEpisodeDialog = false;
        AppStorage.setItemForSession("backUrl", "/app");
    }

    async redirectToDataInsertion(queryParams: { [key: string]: string | number }) {
        await this.router.navigate(["app/formio/data-insertion"], { queryParams });
    }
}
